import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Row, Col } from 'react-flexbox-grid';

import IconMenu from 'material-ui/IconMenu';
import MenuItem from 'material-ui/MenuItem';
import IconButton from 'material-ui/IconButton';
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert';

import ContentWrapper from 'components/ContentWrapper';
import Paper from 'components/Paper';
import { formatDate } from 'lib/dateDisplay';

import FloodlightVisualization from './FloodlightVisualization';

import logo from '../assets/logo.svg';
import journalIcon from '../assets/journal.svg';
import cognitionIcon from '../assets/cognition.svg';
import handFunction from '../assets/hand-function.svg';
import walking from '../assets/walking.svg';

// Feature icons
import mapDataGroups from '../dataMapping';

const logoStyle = {
  height: '8rem',
};

const sectionHeaderStyles = { margin: '4rem 0 1.5rem', paddingLeft: '2rem' };

const sectionHeaderIconStyle = {
  display: 'inline',
  verticalAlign: 'middle',
  height: '3rem',
};

const sectionHeaderTextStyle = {
  display: 'inline',
  verticalAlign: 'middle',
  paddingLeft: '1.2rem',
};

const FloodlightMenu = ({ localeMessage }) => {
  return (
    <IconMenu
      iconButtonElement={
        <IconButton>
          <MoreVertIcon color="#048b82" />
        </IconButton>
      }
      anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
      targetOrigin={{ horizontal: 'left', vertical: 'top' }}
    >
      <MenuItem primaryText={localeMessage('floodlight.unlink')} />
    </IconMenu>
  );
};

FloodlightMenu.propTypes = {
  localeMessage: PropTypes.func,
};

const FloodlightHeader = ({ localeMessage, data }) => (
  <Row style={{ marginTop: '5rem' }} between="xs" middle="xs">
    <Col xs={6}>
      <img style={logoStyle} src={logo} />
    </Col>
    <Col xs={6}>
      <Row end="xs" middle="xs">
        <Col xs={11}>
          <p>
            {localeMessage('floodlight.lastUpdated')}
            {/* Clean this up */}
            &nbsp;&nbsp;&nbsp;
            <span className="bold">
              {data.lastUpdate ? formatDate(data.lastUpdate) : localeMessage('floodlight.nodata')}
            </span>
          </p>
        </Col>
        <Col xs={1}>
          <FloodlightMenu localeMessage={localeMessage} />
        </Col>
      </Row>
    </Col>
  </Row>
);

const wrapperStyles = {
  marginTop: '4rem',
  padding: '2rem 3.5rem',
  backgroundColor: '#f2f6f8',
};

const FloodlightInnerWrapper = ({ children }) => (
  <Row style={wrapperStyles}>
    <Grid fluid style={{ width: '100%', padding: 0 }}>
      {children}
    </Grid>
  </Row>
);

const FloodlightSectionHeader = ({ icon, title, children }) => (
  <Row between="xs" middle="xs" style={sectionHeaderStyles}>
    <Col xs={6}>
      <img style={sectionHeaderIconStyle} src={icon} />
      <h6 style={sectionHeaderTextStyle}>{title}</h6>
    </Col>
    <Col xs={4}>{children}</Col>
  </Row>
);

FloodlightSectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.any.isRequired,
  children: PropTypes.element,
};

FloodlightInnerWrapper.propTypes = {
  children: PropTypes.element,
};

const FloodlightInner = ({ localeMessage, data }) => {
  const mappedData = mapDataGroups(localeMessage, data.groups);
  console.log(mappedData);
  return (
    <ContentWrapper>
      <Paper topMargin={false}>
        <Grid fluid>
          <FloodlightHeader localeMessage={localeMessage} data={data} />
          <FloodlightInnerWrapper>
            <FloodlightSectionHeader title={localeMessage('floodlight.journal')} icon={journalIcon} />
            {mappedData['daily_mood.mood'] && <FloodlightVisualization {...mappedData['daily_mood.mood']} />}
            {mappedData['daily_mood.physical_condition'] && (
              <FloodlightVisualization {...mappedData['daily_mood.physical_condition']} />
            )}
            {mappedData['symptoms_tracker.overview'] && (
              <FloodlightVisualization {...mappedData['symptoms_tracker.overview']} />
            )}
            <FloodlightSectionHeader title={localeMessage('floodlight.cognition')} icon={cognitionIcon} />
            {mappedData['ips.total_score_ips'] && (
              <FloodlightVisualization scaleType="linear" drawZeroLabel {...mappedData['ips.total_score_ips']} />
            )}
            <FloodlightSectionHeader title={localeMessage('floodlight.handfunction')} icon={handFunction} />
            {mappedData['pinch_tomatoes.tomatoes_squeezed_count.L'] && (
              <FloodlightVisualization
                scaleType="linear"
                drawZeroLabel
                {...mappedData['pinch_tomatoes.tomatoes_squeezed_count.L']}
              />
            )}
            {mappedData['pinch_tomatoes.tomatoes_squeezed_count.R'] && (
              <FloodlightVisualization
                scaleType="linear"
                drawZeroLabel
                {...mappedData['pinch_tomatoes.tomatoes_squeezed_count.R']}
              />
            )}
            {mappedData['draw_shape.celerity_shape_attempt.L'] && (
              <FloodlightVisualization
                scaleType="linear"
                drawZeroLabel
                {...mappedData['draw_shape.celerity_shape_attempt.L']}
              />
            )}
            {mappedData['draw_shape.celerity_shape_attempt.R'] && (
              <FloodlightVisualization
                scaleType="linear"
                drawZeroLabel
                {...mappedData['draw_shape.celerity_shape_attempt.R']}
              />
            )}
            <FloodlightSectionHeader title={localeMessage('floodlight.walking')} icon={walking} />
            {mappedData['u_turn.median_turn_speed'] && (
              <FloodlightVisualization scaleType="linear" drawZeroLabel {...mappedData['u_turn.median_turn_speed']} />
            )}
            {mappedData['2mwt.step_intensity'] && (
              <FloodlightVisualization scaleType="linear" drawZeroLabel {...mappedData['2mwt.step_intensity']} />
            )}
          </FloodlightInnerWrapper>
        </Grid>
      </Paper>
    </ContentWrapper>
  );
};

FloodlightInner.propTypes = FloodlightHeader.propTypes = {
  localeMessage: PropTypes.func.isRequired,
  data: PropTypes.objectOf({
    lastUpdate: PropTypes.any,
  }),
};

export default FloodlightInner;
